/**
 * Member utility functions
 *
 * Helper functions for working with member data, including name formatting
 * and lookups.
 */

import type { Member, TeamCompetition } from '../types/api.types';

/**
 * Type for any object that has firstName and surname properties
 */
export interface HasName {
  firstName: string;
  surname: string;
}

/**
 * Format a member's full name
 * @param member - Any object with firstName and surname
 * @returns Formatted full name (firstName surname)
 */
export const formatMemberName = (member: HasName): string => `${member.firstName} ${member.surname}`;

/**
 * Look up a member's name by ID
 * @param members - Array of members
 * @param id - Member ID to look up
 * @returns Formatted member name or null if not found
 */
export const lookupMemberName = (members: Member[] | null | undefined, id: string | null): string | null => {
  if (!members || !members.length || id === null) return null;
  const member = members.find((m) => m.id === id);
  return member ? formatMemberName(member) : null;
};

/**
 * Look up the winning captain's name from a team competition
 * @param members - Array of members
 * @param teamCompetition - Team competition object
 * @returns Winning captain's name or null if not found
 */
export const lookupWinningCaptain = (
  members: Member[] | null | undefined,
  teamCompetition: TeamCompetition | null | undefined
): string | null => {
  if (!members || !members.length) return null;
  if (!teamCompetition) return null;

  const winningCaptainID =
    teamCompetition.winner === teamCompetition.teamA ? teamCompetition.teamACaptainID : teamCompetition.teamBCaptainID;

  // Handle null captain ID
  if (winningCaptainID === null) return null;

  return lookupMemberName(members, winningCaptainID);
};
